import React from 'react';
import { Link } from 'react-router-dom';

// A small Markdown renderer for assistant replies: headings, lists, bold,
// italics, inline code, code blocks and simple tables. It builds React
// elements (never HTML strings), and turns record ids it recognises into links.

export const hrefFor = (c) => {
  switch (c.kind) {
    case 'finding': return `/findings?finding=${encodeURIComponent(c.id)}`;
    case 'ioc': return `/security-alerts?alert=ioc-${c.id}`;
    case 'cve': return `/security-alerts?alert=cve-${c.id}`;
    case 'asset': return `/assets/${c.id}`;
    case 'case': return `/cases/${c.id}`;
    case 'run': return `/playbooks?run=${c.id}`;
    case 'action': return '/response-actions';
    default: return null;
  }
};

// Build token → href from the reply's citations: finding ids, hostnames,
// RUN-XXXXXXXX short ids, case ids and IoC/CVE alert numbers.
export const linkTable = (citations = []) => {
  const map = new Map();
  citations.forEach(c => {
    const href = hrefFor(c);
    if (!href) return;
    if (c.kind === 'finding' || c.kind === 'case') map.set(c.id, href);
    if (c.kind === 'asset' && c.label) map.set(c.label, href);
    if (c.kind === 'run') { map.set(`RUN-${c.id.slice(0, 8).toUpperCase()}`, href); map.set(c.id, href); }
    if (c.kind === 'ioc') map.set(`IoC #${c.id}`, href);
    if (c.kind === 'cve') map.set(`CVE alert #${c.id}`, href);
  });
  return map;
};

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function linkify(text, links, keyBase) {
  const tokens = [...links.keys()].filter(Boolean).sort((a, b) => b.length - a.length);
  const pattern = tokens.length
    ? new RegExp(`(\\bfnd-[0-9a-f]{6,}\\b|${tokens.map(esc).join('|')})`, 'g')
    : /(\bfnd-[0-9a-f]{6,}\b)/g;
  const out = [];
  let last = 0;
  let m;
  let n = 0;
  while ((m = pattern.exec(text))) {
    const tok = m[0];
    // Hostnames only link as whole words.
    const before = text[m.index - 1];
    const after = text[m.index + tok.length];
    if ((before && /[\w-]/.test(before)) || (after && /[\w]/.test(after))) continue;
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = links.get(tok) || (tok.startsWith('fnd-') ? `/findings?finding=${tok}` : null);
    out.push(href
      ? <Link key={`${keyBase}-l${n++}`} to={href} className="font-mono text-[0.93em] hover:underline" style={{ color: 'var(--ink-link)' }}>{tok}</Link>
      : tok);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function inline(text, links, keyBase) {
  // `code`, **bold**, *italic* / _italic_, [label](http…)
  const parts = [];
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|_[^_\s][^_]*_|\[[^\]]+\]\((https?:\/\/[^)\s]+)\))/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(...linkify(text.slice(last, m.index), links, `${keyBase}-t${n}`));
    const t = m[0];
    const k = `${keyBase}-i${n++}`;
    if (t.startsWith('`')) parts.push(<code key={k} className="px-1 py-px rounded font-mono text-[0.9em]" style={{ background: 'var(--surf-raised)' }}>{t.slice(1, -1)}</code>);
    else if (t.startsWith('**')) parts.push(<strong key={k} className="font-semibold">{linkify(t.slice(2, -2), links, k)}</strong>);
    else if (t.startsWith('[')) {
      const label = t.slice(1, t.indexOf(']'));
      parts.push(<a key={k} href={m[2]} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--ink-link)' }}>{label}</a>);
    } else parts.push(<em key={k}>{linkify(t.slice(1, -1), links, k)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) parts.push(...linkify(text.slice(last), links, `${keyBase}-e`));
  return parts;
}

const isTableSep = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const cells = (l) => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());

export default function Markdown({ text, citations, className = '' }) {
  const links = linkTable(citations);
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let i = 0;
  let b = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const key = `b${b++}`;
    if (line.trim().startsWith('```')) {
      const body = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) body.push(lines[i++]);
      i++;
      blocks.push(<pre key={key} className="m-0 px-3 py-2.5 rounded-lg border font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap break-words overflow-x-auto" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>{body.join('\n')}</pre>);
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const size = h[1].length <= 2 ? 'text-[15px]' : 'text-[13.5px]';
      blocks.push(<p key={key} className={`m-0 ${size} font-semibold pt-1`}>{inline(h[2], links, key)}</p>);
      i++;
      continue;
    }
    if (line.includes('|') && lines[i + 1] && isTableSep(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(cells(lines[i++]));
      blocks.push(
        <div key={key} className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--surf-line)' }}>
          <table className="w-full border-collapse text-[12.5px]">
            <thead><tr>{head.map((c, j) => <th key={j} className="text-left font-semibold px-2.5 py-1.5 border-b" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>{inline(c, links, `${key}h${j}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, j) => <td key={j} className="px-2.5 py-1.5 border-b align-top" style={{ borderColor: 'var(--surf-line)' }}>{inline(c, links, `${key}r${ri}c${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]/.test(line);
      const items = [];
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i++;
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) { items[items.length - 1] += ` ${lines[i].trim()}`; i++; }
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(<Tag key={key} className={`m-0 pl-5 flex flex-col gap-1 ${ordered ? 'list-decimal' : 'list-disc'}`}>{items.map((it, j) => <li key={j}>{inline(it, links, `${key}-${j}`)}</li>)}</Tag>);
      continue;
    }
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|```|\s*([-*•]|\d+[.)])\s+)/.test(lines[i]) && !(lines[i].includes('|') && lines[i + 1] && isTableSep(lines[i + 1]))) para.push(lines[i++]);
    blocks.push(<p key={key} className="m-0">{inline(para.join(' '), links, key)}</p>);
  }
  return <div className={`flex flex-col gap-2.5 text-[13.5px] leading-relaxed ${className}`}>{blocks}</div>;
}
